import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import clockGif from "../../public/gifs/clock.gif";
import { Text } from "@/components/Text";

// CDN path for spin the wheel assets
const CDN_ROOT_URL = process.env.NEXT_PUBLIC_CDN_ROOT_URL || "";
const STW_CDN_PATH = "/subscription/spin_the_wheel_assets";
const claimOfferCardEn = `${CDN_ROOT_URL}${STW_CDN_PATH}/stw-claim-offer-card-en.png`;
const claimOfferCardHi = `${CDN_ROOT_URL}${STW_CDN_PATH}/stw-claim-offer-card-hi.png`;
import {
  AppRoutesEnum,
  CookiesEnum,
  cookiesValues,
  EventNamesEnum,
  GamificationPlanIdEnum,
  GAMIFICATION_PLAN_ID_TO_ROUTE,
  LangEnum,
  LocalStorageKeysEnum,
  SUBSCRIPTION_STATUS,
  URLQueryParamsEnum,
} from "@/constants/constants";
import { useUserContext } from "@/context/UserProvider";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { getClientSideCookieValue } from "@/utils/common.helpers";

interface ExpiresInTimerProps {
  expiresInSeconds?: number;
  variant?: "text" | "pill" | "widget";
  onExpired?: () => void;
}

export const ExpiresInTimer = ({
  expiresInSeconds = 0,
  variant = "text",
  onExpired,
}: ExpiresInTimerProps) => {
  const { t, currentLanguage } = useClientTranslations();
  const { trackEvent } = useTrackingAnalytics();
  const [expiresIn, setExpiresIn] = useState(expiresInSeconds);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [expiresInText, setExpiresInText] = useState("00:00s");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const { isTrialEligibleUser, subscriptionStatus } = useUserContext();

  const [contentDetailURL, setContentDetailURL] = useState<string | null>(null);
  const [gamificationURL, setGamificationURL] = useState<string>(
    `${AppRoutesEnum.PAYWALL}/${GAMIFICATION_PLAN_ID_TO_ROUTE[GamificationPlanIdEnum.SPIN_THE_WHEEL]}`,
  );
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const storedContentDetailURL = localStorage.getItem(
      LocalStorageKeysEnum.SOURCE_CONTENT_BEFORE_PAYING,
    );
    const contentId = localStorage.getItem(
      LocalStorageKeysEnum.SOURCE_CONTENT_ID_BEFORE_PAYING,
    );
    const contentType = localStorage.getItem(
      LocalStorageKeysEnum.SOURCE_CONTENT_TYPE_BEFORE_PAYING,
    );
    const cookieToken = getClientSideCookieValue(
      cookiesValues[CookiesEnum.Token],
    );

    // Get the trialPlanId cookie to determine which gamification feature to redirect to
    const trialPlanId = getClientSideCookieValue(
      cookiesValues[CookiesEnum.TrialPlanId],
    ) as GamificationPlanIdEnum | null;

    setToken(cookieToken || null);

    if (storedContentDetailURL) {
      setContentDetailURL(storedContentDetailURL);
    }

    // Determine the correct gamification route based on trialPlanId
    // Default fallback to spin-the-wheel if no valid trialPlanId is found
    let gamificationRoute =
      GAMIFICATION_PLAN_ID_TO_ROUTE[GamificationPlanIdEnum.SPIN_THE_WHEEL];
    if (
      trialPlanId &&
      Object.values(GamificationPlanIdEnum).includes(trialPlanId)
    ) {
      gamificationRoute = GAMIFICATION_PLAN_ID_TO_ROUTE[trialPlanId];
    }

    if (contentId && contentType) {
      const base = `${AppRoutesEnum.PAYWALL}/${gamificationRoute}?${URLQueryParamsEnum.CONTENT_ID}=${encodeURIComponent(
        contentId,
      )}&${URLQueryParamsEnum.CONTENT_TYPE}=${encodeURIComponent(contentType)}`;
      setGamificationURL(base);
    } else {
      setGamificationURL(`${AppRoutesEnum.PAYWALL}/${gamificationRoute}`);
    }
  }, []);

  // Inside ExpiresInTimer component render
  const href =
    token && contentDetailURL
      ? `${AppRoutesEnum.CHECKOUT}?${URLQueryParamsEnum.REDIRECTION_AFTER_PAYMENT_TO}=${encodeURIComponent(
          contentDetailURL,
        )}`
      : gamificationURL;

  useEffect(() => {
    setIsSubscribed(subscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIBED);
  }, [subscriptionStatus]);

  // Show component after a delay to ensure values are loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldShow(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Clean up timer when component should not render
  useEffect(() => {
    if (!isTrialEligibleUser || isSubscribed) {
      if (timer) {
        clearInterval(timer);
      }
      setTimer(null);
    }
  }, [isTrialEligibleUser, isSubscribed, timer]);

  useEffect(() => {
    // Don't start timer if user is not eligible or is subscribed
    if (!isTrialEligibleUser || isSubscribed) {
      return;
    }

    // Reset the expiresIn state when prop changes
    setExpiresIn(expiresInSeconds);

    // Initialize the text format
    const minutes = Math.floor(expiresInSeconds / 60);
    const seconds = expiresInSeconds % 60;
    setExpiresInText(
      `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}s`,
    );

    // Don't start timer if expiresInSeconds is 0 or negative
    if (expiresInSeconds <= 0) {
      return;
    }

    // Set up the interval
    const intervalId = setInterval(() => {
      setExpiresIn((prev) => {
        const newExpiresIn = prev - 1;
        const minutes = Math.floor(newExpiresIn / 60);
        const seconds = newExpiresIn % 60;
        setExpiresInText(
          `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}s`,
        );

        if (newExpiresIn <= 0) {
          if (intervalId) {
            clearInterval(intervalId);
          }
          setExpiresInText("00:00s");
          onExpired?.();
        }

        return newExpiresIn;
      });
    }, 1000);

    setTimer(intervalId);

    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [expiresInSeconds, isTrialEligibleUser, isSubscribed, onExpired]);

  if (!shouldShow || !isTrialEligibleUser || isSubscribed) {
    return null;
  }

  const handleWidgetClick = () => {
    trackEvent({
      eventName: EventNamesEnum.TITLE_OFFER_CLICKED,
      properties: {},
      sendTo: [],
    });
  };

  const renderContent = () => {
    const content = (
      <>
        <Image src={clockGif} alt="clock" width={20} height={20} />
        <Text size={3} appearance="primary" weight="bold">
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                expiresIn > 0
                  ? "spin_the_wheel.expires_in_text"
                  : "spin_the_wheel.limited_offer",
              ).replace("%d", expiresInText),
            }}
          />
        </Text>
      </>
    );

    switch (variant) {
      case "text":
        return (
          <div className="flex items-center gap-1 transition-opacity duration-300">
            {content}
          </div>
        );
      case "pill":
        return (
          <div
            className={`flex items-center justify-center rounded-full bg-stg-maroon px-2.5 py-1.5 transition-opacity duration-300 md:hidden ${expiresIn > 0 ? "opacity-100" : "opacity-0"}`}
          >
            {content}
          </div>
        );
      case "widget":
        return (
          <Link href={href} prefetch={true} onClick={handleWidgetClick}>
            <div className="animate-shake-twice absolute -top-[3.5rem] left-0 z-10">
              <Image
                src={
                  currentLanguage === LangEnum.HI
                    ? claimOfferCardHi
                    : claimOfferCardEn
                }
                alt="claim-offer-card"
                width={100}
                height={100}
              />
            </div>
            <div
              className={`relative flex flex-col items-center justify-end from-stg-red-6 shadow-widget-card md:hidden ${expiresIn > 0 ? "gap-1" : "gap-0"} pt-7x rounded-lg bg-gradient-to-b to-stg-maroon p-2 pt-7 opacity-80 backdrop-blur-sm`}
            >
              <Text size={3} appearance="primary" weight="bold">
                {t("spin_the_wheel.claim_now_cta")}
              </Text>
              <div
                className={`flex items-center overflow-hidden transition-all duration-300 ${expiresIn > 0 ? "max-h-8 opacity-100" : "max-h-0 opacity-0"}`}
              >
                <Image src={clockGif} alt="clock" width={20} height={20} />
                <Text size={1} appearance="primary" weight="bold">
                  {expiresInText}
                </Text>
              </div>
            </div>
          </Link>
        );
      default:
        return null;
    }
  };

  return renderContent();
};
